// src/components/common/AiCopyNotice.jsx
// Soft, dismissable nudge reminding users that AI-generated copy is a starting
// point — the platforms reward original, personal writing and quietly demote
// generic posts. Shared by the Landing Page Builder (queue item #13) and the
// publish-preview caption editor (#14).
//
// Both surfaces read/write ONE permanent dismiss flag, so ticking "Don't show
// this again" in either place silences it everywhere, for good (Brett's call).
// The flag lives in localStorage, keyed per user (u_id) so a shared browser
// doesn't leak one user's choice to the next login.
//
// Two dismiss actions, by design:
//   ×                    → hide for this view only; returns on the next visit.
//   "Don't show again"   → permanent (writes the flag).
//
// Positioning is CALLER-controlled via `className` on the outer element — an
// inline banner in the Landing Page Builder (`m-4`), a floating toast pinned
// above the footer in the publish modal (`absolute ... bottom-full`). The inner
// card owns the visuals + `relative` so the × anchors correctly in both.

import React, { useState } from 'react';
import { useProfileStore } from '../../state/profileStore';

const BASE_KEY = 'eh_ai_copy_notice_dismissed';
const keyFor = (uid) => (uid ? `${BASE_KEY}_${uid}` : BASE_KEY);

function readDismissed(uid) {
  try {
    return localStorage.getItem(keyFor(uid)) === '1';
  } catch {
    return false;
  }
}

export default function AiCopyNotice({ className = '' }) {
  const uid = useProfileStore((s) => s.profile?.u_id);
  const [hidden, setHidden] = useState(() => readDismissed(uid));

  if (hidden) return null;

  const silenceForever = () => {
    try {
      localStorage.setItem(keyFor(uid), '1');
    } catch {
      /* private mode / quota — fall back to session-only hide */
    }
    setHidden(true);
  };

  return (
    <div className={className}>
      <div className="relative flex items-start gap-2 rounded-lg border border-[#00aef7]/40 bg-white shadow-md px-3 py-2.5">
        <svg className="w-4 h-4 text-[#00aef7] flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
        <div className="min-w-0 flex-1 pr-4">
          <p className="text-[11px] text-gray-700 leading-snug">
            AI gives you a solid first draft — but the platforms reward original, personal
            copy and quietly demote generic posts. Tweak this to sound like you to protect your reach.
          </p>
          <label className="mt-1.5 inline-flex items-center gap-1.5 text-[10px] text-gray-500 cursor-pointer select-none">
            <input
              type="checkbox"
              onChange={(e) => { if (e.target.checked) silenceForever(); }}
              className="h-3 w-3 rounded border-gray-300 text-[#00aef7] focus:ring-[#00aef7] focus:ring-offset-0"
            />
            Don&apos;t show this again
          </label>
        </div>
        <button
          type="button"
          onClick={() => setHidden(true)}
          aria-label="Dismiss"
          className="absolute top-1.5 right-2 text-gray-400 hover:text-gray-600 leading-none text-base"
        >
          ×
        </button>
      </div>
    </div>
  );
}
